import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';

export const SectionWrapper = ({ 
  children, 
  title, 
  subtitle, 
  description, 
  stepNumber, 
  isActive = true,
  isCompleted = false,
  className,
  showImage = false,
  image = null
}) => {
  const { isRTL } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <motion.section 
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
      className={cn(
        'py-8 md:py-12 px-4 relative',
        isActive ? 'opacity-100' : 'opacity-60',
        className
      )}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-radial from-accent/5 to-transparent blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-6 md:mb-8">
          {/* Step Badge */}
          {stepNumber && (
            <motion.div 
              className="flex items-center justify-center gap-3 mb-4"
              whileHover={{ scale: 1.02 }}
            >
              <div className={cn(
                'step-badge',
                isCompleted ? 'completed' : isActive ? 'active' : 'pending'
              )}>
                {isCompleted ? (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    ✓
                  </motion.span>
                ) : (
                  stepNumber
                )}
              </div>
              <div className="text-start">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-display text-foreground">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {subtitle}
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* Description */}
          {description && (
            <motion.p 
              variants={itemVariants}
              className="text-muted-foreground max-w-2xl mx-auto text-lg"
            >
              {description}
            </motion.p>
          )}
        </motion.div>

        {/* Optional Image */}
        {showImage && image && (
          <motion.div 
            variants={itemVariants}
            className="mb-8 flex justify-center"
          >
            <div className="product-showcase w-full max-w-md aspect-video">
              <img src={image} alt="" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        )}

        {/* Section Content */}
        <motion.div variants={itemVariants}>
          {children}
        </motion.div>
      </div>
    </motion.section>
  );
};
